import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
  ViewQueryRecordNode,
} from '@salescore/core'
import type { CSSProperties, ReactElement, RefObject } from 'react'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import type { RSheetsCursor } from '../../../domain/cursor/cursorTypes'
import { type CursorPosition, useCursorMutation, useSetMoveCursor } from '../../../recoil/cursor'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import type { RecordNodeWithInnerRowIndex } from '../../util/innerRowRelated'
import { RSheetsStyle } from '../../util/RSheetsStyle'
import { InputCell } from '../body/cell/InputCell'

/**
 * セルが入力モード (クリック / Enter 等で `isEditing` が立った状態) のときに表示される
 * カーソル。中身は `InputCell` (カラムの型ごとの入力コンポーネント) をレンダリングします。
 *
 * 表示用のセルコンポーネントとは別箇所にあえて配置しているのは、セル側で実装するとボディの
 * zIndex に縛られ、条件によって最前面に出ないことがあるためです。`position: absolute` で
 * 別レイヤーに浮かせて、常に前面に表示します。
 */
export const InputCursorBox = ({
  rootReference,
  cursor: {
    main: { innerRowIndex, rowIndex },
  },
  column,
  row,
  position: { height, width, x, y },
  recordNodeWithInnerRowIndex: { parentRecordNode, recordNode },
  headerHeight,
  fixedLeftColumnIndex,
  columns,
  compiledConfig,
  ability,
  query,
  visibleRowNumber,
}: {
  rootReference: RefObject<HTMLDivElement>
  cursor: RSheetsCursor
  column: RSheetColumn<RSheetRecordNode>
  row: ViewQueryRecordNode
  position: CursorPosition
  recordNodeWithInnerRowIndex: RecordNodeWithInnerRowIndex
  headerHeight: number
  fixedLeftColumnIndex: number
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList | undefined
  visibleRowNumber: number | undefined
}): ReactElement => {
  const { finishEditMode } = useCursorMutation(ability)
  const moveCursor = useSetMoveCursor(fixedLeftColumnIndex, columns, compiledConfig, ability, visibleRowNumber)

  const onFinishEditMode = (option?: { moveRowDiff?: number; moveColumnDiff?: number }): void => {
    if (option?.moveRowDiff !== undefined || option?.moveColumnDiff !== undefined) {
      moveCursor({
        columnDiff: option.moveColumnDiff ?? 0,
        rowDiff: option.moveRowDiff ?? 0,
        expand: false,
      })
    } else {
      finishEditMode()
    }
    if (rootReference.current !== null) {
      rootReference.current.focus()
    }
  }

  const style: CSSProperties = {
    position: 'absolute',
    top: y + headerHeight,
    left: x,
    width,
    height,
    borderRadius: 4,
    border: `2px #3274E0 solid`,
    boxShadow: '1px 4px 7px 2px rgb(60 64 67 / 15%)',
    zIndex: RSheetsStyle.zIndex.cursorWhenLeftFixed, // 編集の場合、常に最上位にする
  }

  return (
    <InputCell
      key={[recordNode?.id, rowIndex, innerRowIndex].join(',')}
      recordNode={recordNode}
      parentRecordNode={parentRecordNode}
      rootRecordNode={row}
      column={column}
      rowIndex={rowIndex}
      innerRowIndex={innerRowIndex}
      defaultWidth={width}
      boxStyle={style}
      finishEditMode={onFinishEditMode}
      columns={columns}
      compiledConfig={compiledConfig}
      ability={ability}
      query={query}
    />
  )
}
